import { useEffect } from 'react';

import { logDataDogEvent } from 'utils/datadog';

import {
  ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT,
  ZOOM_CHAT_ESCALATION_END_TIMEOUT_MS,
  ZOOM_CHAT_ESCALATION_SDK_TIMEOUT_MS,
  ZOOM_CHAT_WEBSITE_DATA_KEYS
} from './zoomChat.constants';
import { onZoomChatSdkAvailable } from './zoomChatSdk';
import { createTraceId } from './zoomChatTelemetry';

export type ZoomChatEscalationOutcome =
  | 'already-chatting'
  | 'cancelled'
  | 'open-rejected'
  | 'open-unsupported'
  | 'opened'
  | 'replace-failed'
  | 'sdk-unavailable';

export type ZoomChatEscalationResult = {
  outcome: ZoomChatEscalationOutcome;
  traceId: string;
};

/**
 * `open` is typed here and not in `global.d.ts`: this module is its only caller, and the live
 * test that exercised it (Aug 21-26, see escalateToZoomChat) ran against one campaign bundle. The
 * optional marks the honest state: a runtime whose bundle predates the option simply does not
 * have the method.
 */
type ZoomCampaignSdkWithOpen = ZoomCampaignSdk & {
  open?: (options?: { alwaysStartChat?: boolean }) => unknown;
};

/**
 * Removes the routing hints. Called when an engagement ends, so the escalation applies to
 * exactly the engagement the Connect tap opened: a patient who chats again afterwards — via the
 * bubble or a fresh escalation — starts from a clean slate, and a stale `escalation` flag cannot
 * silently skip the topic menu on an ordinary chat. Also part of the identity sweep on logout,
 * because the summary text is patient context and must not outlive the session on a shared
 * device.
 */
export const clearZoomChatRoutingHints = () => {
  try {
    Object.values(ZOOM_CHAT_WEBSITE_DATA_KEYS).forEach((key) =>
      window.localStorage.removeItem(key)
    );
  } catch {
    // Same tolerance as the writer: storage failures must never break the page.
  }
};

// Clears the hints only while they are still the given attempt's, so an escalation whose open
// failed late never erases a newer hand-off's hints (weekend review, Sept 28).
const clearZoomChatRoutingHintsOf = (attempt: string) => {
  try {
    if (window.localStorage.getItem(ZOOM_CHAT_WEBSITE_DATA_KEYS.attempt) === attempt) {
      clearZoomChatRoutingHints();
    }
  } catch {
    // Same tolerance as the writer.
  }
};

// Bumped by every identity sweep (a sign-out in any tab, or a different patient signing in): a
// Connect tap from before it must not write patient context or open a chat after it. The sweep
// also cancels whatever is still waiting for the SDK (weekend review, Sept 28).
let escalationSession = 0;
const pendingEscalations = new Set<() => void>();

export const cancelPendingZoomChatEscalations = () => {
  escalationSession += 1;
  [...pendingEscalations].forEach((cancel) => cancel());
};

/**
 * Writes the ZCC flow's routing hints into localStorage — the channel the flow's custom
 * variables read via their "From website data → Local storage" binding.
 *
 * Called by `escalateToZoomChat` just before `open()`, at Connect-tap time only: the flow reads
 * website data when the engagement starts, after the tap. Best-effort — a routing hint must never
 * break the page or the escalation.
 *
 * Every earlier hint is cleared first, so a value this hand-off does not carry can never ride
 * along from an older one. No thread id is written: it only served the flow's pull from
 * /escalation-summary, which V1 does not have, and the agent's context arrives as the summary and
 * the first message instead.
 */
export const writeZoomChatRoutingHints = ({
  attempt,
  firstMessage,
  summaryHint
}: {
  /** The escalation writing them, so only it clears them if its open fails. */
  attempt?: string;
  firstMessage?: string;
  summaryHint?: string;
}) => {
  clearZoomChatRoutingHints();

  try {
    if (attempt) {
      window.localStorage.setItem(ZOOM_CHAT_WEBSITE_DATA_KEYS.attempt, attempt);
    }
    window.localStorage.setItem(ZOOM_CHAT_WEBSITE_DATA_KEYS.escalation, 'true');
    if (summaryHint) {
      window.localStorage.setItem(
        ZOOM_CHAT_WEBSITE_DATA_KEYS.summaryHint,
        summaryHint.slice(0, 500)
      );
    }
    if (firstMessage) {
      window.localStorage.setItem(
        ZOOM_CHAT_WEBSITE_DATA_KEYS.firstMessage,
        firstMessage.slice(0, 500)
      );
    }
  } catch {
    // localStorage can throw (private mode, quota); the engagement still opens without hints.
  }
};

/**
 * Clears the routing hints whenever an engagement ends, from either side — patient closing the
 * chat or agent ending it. Mounted with the SDK loader (the events only exist once the runtime
 * is up), mirroring the identity watchdog's subscription shape, including surviving a vendor
 * emitter that rejects the subscription.
 */
// One listener per SDK, for the SDK's whole life. The widget unmounts whenever a mount gate drops
// or the app shows its loader, while the engagement it started can go on, so a listener tied to the
// mount would miss an agent ending the engagement then and leave the hints for the next one
// (weekend review, Sept 28).
const sdksWithHintsCleanup = new WeakSet<object>();

export const useZoomChatRoutingHintsCleanup = () => {
  useEffect(
    () =>
      onZoomChatSdkAvailable((sdk) => {
        if (sdksWithHintsCleanup.has(sdk)) return;

        try {
          sdk.on(ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT, clearZoomChatRoutingHints);
          sdksWithHintsCleanup.add(sdk);
        } catch {
          // A rejected subscription leaves stale hints until logout sweeps them — degraded, not
          // broken, and the identity sweep still guarantees they never cross patients.
        }
      }),
    []
  );
};

/**
 * Hands an ended chatbot conversation over to a Zoom Contact Center agent by opening the Zoom
 * widget and starting an engagement (proven live Aug 21-26: `open({ alwaysStartChat: true })`
 * starts a routable engagement and the hints route it past the topic menu).
 *
 * The browser does not decide this. The only caller is the conversation panel's Connect control,
 * which renders only while the chat backend's live-agent offer stands — stamped once the hand-off
 * wrote its platform task (LMD-1484) — so the task stays the record and the fallback, and nothing
 * here changes the AI conversation's participants or its exit.
 *
 * A Zoom chat that is already open, the routed one an earlier Connect started, or one the patient
 * started from the corner bubble, is replaced (LMD-2222): it is ended, and the new one opens with
 * this hand-off's hints once the old one reports its end. `onReplacingLiveChat` fires first, so the
 * control can tell the patient what is happening.
 *
 * Resolves, never rejects: an escalation that fails must degrade into the request staff already
 * have, not into an error boundary over a patient who is already having a bad time.
 */
export const escalateToZoomChat = ({
  firstMessage,
  onReplacingLiveChat,
  reason,
  signal,
  summaryHint
}: {
  /** The pathway's greeting for the agent (Zoom's /handoff reply); agent-only context. */
  firstMessage?: string;
  /**
   * Called once, just before a Zoom chat that is already open is ended to make way for this one,
   * so the control can show the patient the notice while the old chat ends and the new one opens.
   */
  onReplacingLiveChat?: () => void;
  reason: string;
  /** Aborted when the Connect control goes away: a tap nobody can see must not open a chat later. */
  signal?: AbortSignal;
  /** The hand-off's summary, pushed as website data; agent-only context. */
  summaryHint?: string;
}): Promise<ZoomChatEscalationResult> => {
  const traceId = createTraceId();
  const session = escalationSession;

  return new Promise<ZoomChatEscalationResult>((resolve) => {
    let settled = false;
    let timeoutId: number | undefined;
    let stopWaiting: () => void = () => {};
    // Ends the wait for an open chat's end: the timer and the `engagement_ended` listener. A tap
    // cancelled or settled mid-wait must not leave them to fire a failure nobody is waiting for.
    let stopReplacing: () => void = () => {};
    // Whether this attempt has written its routing hints (openRouted below), and whether the
    // vendor open it then issued has resolved or rejected.
    let hintsWritten = false;
    let openSettled = false;

    const settle = (outcome: ZoomChatEscalationOutcome) => {
      if (settled) return;
      settled = true;
      pendingEscalations.delete(cancel);
      signal?.removeEventListener('abort', cancel);
      window.clearTimeout(timeoutId);
      stopWaiting();
      stopReplacing();

      // An attempt that wrote its hints and did not open takes them back (audit, Oct 1): a vendor
      // open that never settles ends in a timeout here, and hints left behind would route the
      // patient's next ordinary chat as an escalation, with a stale summary. Attempt-scoped, so a
      // newer hand-off's hints survive a late settle.
      //
      // A cancelled tap (the control unmounted, a sign-out) with an open still in flight is the
      // one case decided later: the open may yet succeed, and the chat it starts needs the hints.
      // So they stay for as long as the open itself was given; a late success keeps them, a late
      // rejection or no answer at all clears them (recheck, Oct 2).
      if (hintsWritten && outcome !== 'opened') {
        if (outcome !== 'cancelled') {
          clearZoomChatRoutingHintsOf(traceId);
        } else if (!openSettled) {
          window.setTimeout(() => {
            if (!openSettled) clearZoomChatRoutingHintsOf(traceId);
          }, ZOOM_CHAT_ESCALATION_SDK_TIMEOUT_MS);
        }
      }

      logDataDogEvent({
        context: { feature: 'zoom-chat-escalation', outcome, reason, traceId },
        level: outcome === 'opened' || outcome === 'already-chatting' ? 'info' : 'warn',
        message: 'Zoom chat escalation outcome'
      });

      resolve({ outcome, traceId });
    };

    // A sign-out, a patient switch or the Connect control going away while the SDK is still on
    // its way: stop waiting, and never write or open anything for this tap.
    function cancel() {
      settle('cancelled');
    }

    if (signal?.aborted) {
      cancel();

      return;
    }

    pendingEscalations.add(cancel);
    signal?.addEventListener('abort', cancel, { once: true });

    // Armed BEFORE subscribing, because the subscription callback runs SYNCHRONOUSLY when the
    // runtime is already published — the warm-load case, which is the commonest one — and it
    // settles through the handles declared above.
    timeoutId = window.setTimeout(
      () => settle('sdk-unavailable'),
      ZOOM_CHAT_ESCALATION_SDK_TIMEOUT_MS
    );

    stopWaiting = onZoomChatSdkAvailable((sdk) => {
      if (settled) return;

      if (session !== escalationSession) {
        settle('cancelled');

        return;
      }

      const { open } = sdk as ZoomCampaignSdkWithOpen;

      // Brings the open chat's window up with a plain `open()`, no second engagement and no hints:
      // with it collapsed, a Connect tap showed the patient nothing (dev preview, Sept 30), and a
      // plain open is proven to surface it without starting another (same day). Best effort: the
      // outcome is the same whether or not the window came up; a failure is only logged.
      const surfaceLiveEngagement = (
        outcome: Extract<ZoomChatEscalationOutcome, 'already-chatting' | 'replace-failed'>
      ) => {
        if (typeof open !== 'function') {
          settle(outcome);

          return;
        }

        const surfaceFailed = (error: unknown) => {
          logDataDogEvent({
            context: { feature: 'zoom-chat-escalation', reason, traceId },
            error: error instanceof Error ? error : new Error('Zoom chat open rejected'),
            level: 'warn',
            message: 'Zoom chat escalation could not surface the live engagement'
          });
          settle(outcome);
        };

        try {
          // Same `Promise.resolve` reasoning as the open below: a synchronous throw and a
          // rejection must take the same path.
          void Promise.resolve(open.call(sdk))
            .then(() => settle(outcome))
            .catch(surfaceFailed);
        } catch (error) {
          surfaceFailed(error);
        }
      };

      // Hand the ZCC flow its routing hints, then start the engagement. The flow's custom
      // variables read localStorage (their "From website data" binding), so writing these keys
      // BEFORE `open()` is the load-bearing path: `escalation` makes the flow skip the
      // virtual-agent menu and route to a live agent, and `summaryHint`/`firstMessage` are the
      // agent-facing pair. No `updateWebsiteData` fallback: this bundle's runtime rejects it
      // outright ("only supported by Chat SDK V3", observed live 2026-08-26), so the call could
      // never deliver anything. Written only now, for an open that is about to happen, and
      // cleared again if it fails, so a later ordinary chat never inherits them (weekend review,
      // Sept 28).
      const openRouted = (start: NonNullable<ZoomCampaignSdkWithOpen['open']>) => {
        writeZoomChatRoutingHints({ attempt: traceId, firstMessage, summaryHint });
        hintsWritten = true;

        const failed = () => {
          openSettled = true;
          clearZoomChatRoutingHintsOf(traceId);
          settle('open-rejected');
        };

        try {
          // `Promise.resolve` for the same reason `endZoomChatEngagement` uses it: the return
          // shape is the vendor's, so a synchronous throw and a rejection must take the same path.
          void Promise.resolve(start.call(sdk, { alwaysStartChat: true }))
            .then(() => {
              openSettled = true;
              settle('opened');
            })
            .catch(failed);
        } catch {
          failed();
        }
      };

      if (sdk.isChatting) {
        // A runtime without `open` can neither start a chat nor bring one up: the open chat stands,
        // whichever it is, and that is the outcome.
        if (typeof open !== 'function') {
          settle('already-chatting');

          return;
        }

        // A Zoom chat is already open, the routed one an earlier Connect started, or one the
        // patient started from the corner bubble (the virtual agent's topic menu, stage Oct 1, or a
        // person), and it does not carry this hand-off's summary and greeting. Both kinds are
        // replaced (LMD-2222): the open chat is ended, and the routed one opens only once THAT chat
        // reports its end (proven live on stage, Oct 1, for the bubble chat: `endChat()`,
        // `engagement_ended`, then `open({ alwaysStartChat: true })` queued for an agent with the
        // hints). The open waits a turn after the event, so the cleanup that clears hints on the
        // same event has run first whatever order the listeners fire in. No end event in time, a
        // rejected `endChat` or a subscription that throws all leave the open chat brought up
        // instead: a second engagement over a live one is Zoom-side undefined behaviour.
        //
        // The caller hears first, so the patient reads what is happening before the chat goes.
        onReplacingLiveChat?.();
        logDataDogEvent({
          context: { feature: 'zoom-chat-escalation', reason, traceId },
          level: 'info',
          message: 'Zoom chat escalation is ending the open chat to replace it'
        });

        // The SDK has arrived, so its timer no longer applies; this path gets its own bound, for
        // the end and the open together, so a vendor call that never settles still settles the tap.
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(
          () => settle('open-rejected'),
          ZOOM_CHAT_ESCALATION_SDK_TIMEOUT_MS
        );

        let done = false;
        let endTimer: number | undefined;
        const stopListening = () => {
          done = true;
          window.clearTimeout(endTimer);
          try {
            sdk.off(ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT, onEnded);
          } catch {
            // Unsubscribing is best effort; `done` already makes a late event a no-op.
          }
        };
        const endFailed = (error: unknown) => {
          if (done) return;
          stopListening();
          logDataDogEvent({
            context: { feature: 'zoom-chat-escalation', reason, traceId },
            error: error instanceof Error ? error : new Error('Zoom chat end failed'),
            level: 'warn',
            message: 'Zoom chat escalation could not end the live chat'
          });

          if (!settled) surfaceLiveEngagement('replace-failed');
        };
        function onEnded() {
          if (done) return;
          stopListening();
          // A turn later, so every listener of this same event (the hint cleanup among them) has
          // run before the hints for the new chat are written.
          window.setTimeout(() => {
            if (settled) return;

            if (session !== escalationSession) {
              settle('cancelled');

              return;
            }

            openRouted(open as NonNullable<ZoomCampaignSdkWithOpen['open']>);
          }, 0);
        }

        try {
          sdk.on(ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT, onEnded);
        } catch (error) {
          endFailed(error);

          return;
        }

        endTimer = window.setTimeout(
          () => endFailed(new Error('Zoom chat did not report the end in time')),
          ZOOM_CHAT_ESCALATION_END_TIMEOUT_MS
        );
        stopReplacing = () => {
          if (!done) stopListening();
        };

        try {
          void Promise.resolve(sdk.endChat()).catch(endFailed);
        } catch (error) {
          endFailed(error);
        }

        return;
      }

      if (typeof open !== 'function') {
        settle('open-unsupported');

        return;
      }

      openRouted(open);
    });
  });
};
